/**
 * AddressFields — reusable structured-address input.
 *
 * **The Bloq standard for physical addresses** (used app-wide):
 *   line1, line2 (optional), city, state, postal_code, country
 *
 * Anywhere the app captures a physical address it MUST use this component
 * so the shape, validation, geocoding inputs, and display are consistent.
 *
 * Props:
 *   value   — { line1, line2, city, state, postal_code, country }
 *   onChange(next) — receives the same shape on every keystroke / select.
 *   testid  — base test-id; per-field ids derive as `${testid}-line1` etc.
 *
 * Country/state dropdowns:
 *   - Country dropdown is a curated short list (US, CA, GB, AU, plus "Other"
 *     which switches state to free-text).
 *   - For US + CA, the state dropdown auto-populates with subdivisions.
 *   - For other countries, "State / Province / Region" becomes a text input.
 *
 * Geocoding:
 *   - The component does NOT geocode — that lives server-side. Backends
 *     receive the structured payload, compose a single "line1, line2,
 *     city, state postal_code, country" string, and pass it to the geocoder.
 */
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Full list of countries (ISO 3166-1 alpha-2) used by the country dropdown.
 * US territories (PR, GU, VI, AS, MP) are surfaced as separate entries
 * AND auto-fill into the state dropdown when "US" is picked — so a visitor
 * in Puerto Rico can either pick "Puerto Rico" as their country (preferred
 * for international shipping context) OR pick "United States" + state "PR".
 *
 * Ordered roughly by reach (English-speaking first, then alpha). This is
 * intentional: most invitees today are US/UK/CA/AU — surfacing those at
 * the top avoids a 200-entry scroll for ~80% of bookings.
 */
const COUNTRIES = [
  { code: "US", label: "United States" },
  { code: "CA", label: "Canada" },
  { code: "GB", label: "United Kingdom" },
  { code: "AU", label: "Australia" },
  { code: "__DIVIDER__", label: "──────────" },
  { code: "AF", label: "Afghanistan" }, { code: "AX", label: "Åland Islands" }, { code: "AL", label: "Albania" },
  { code: "DZ", label: "Algeria" }, { code: "AS", label: "American Samoa" }, { code: "AD", label: "Andorra" },
  { code: "AO", label: "Angola" }, { code: "AI", label: "Anguilla" }, { code: "AQ", label: "Antarctica" },
  { code: "AG", label: "Antigua and Barbuda" }, { code: "AR", label: "Argentina" }, { code: "AM", label: "Armenia" },
  { code: "AW", label: "Aruba" }, { code: "AT", label: "Austria" }, { code: "AZ", label: "Azerbaijan" },
  { code: "BS", label: "Bahamas" }, { code: "BH", label: "Bahrain" }, { code: "BD", label: "Bangladesh" },
  { code: "BB", label: "Barbados" }, { code: "BY", label: "Belarus" }, { code: "BE", label: "Belgium" },
  { code: "BZ", label: "Belize" }, { code: "BJ", label: "Benin" }, { code: "BM", label: "Bermuda" },
  { code: "BT", label: "Bhutan" }, { code: "BO", label: "Bolivia" }, { code: "BQ", label: "Bonaire, Sint Eustatius and Saba" },
  { code: "BA", label: "Bosnia and Herzegovina" }, { code: "BW", label: "Botswana" }, { code: "BV", label: "Bouvet Island" },
  { code: "BR", label: "Brazil" }, { code: "IO", label: "British Indian Ocean Territory" }, { code: "BN", label: "Brunei Darussalam" },
  { code: "BG", label: "Bulgaria" }, { code: "BF", label: "Burkina Faso" }, { code: "BI", label: "Burundi" },
  { code: "CV", label: "Cabo Verde" }, { code: "KH", label: "Cambodia" }, { code: "CM", label: "Cameroon" },
  { code: "KY", label: "Cayman Islands" }, { code: "CF", label: "Central African Republic" }, { code: "TD", label: "Chad" },
  { code: "CL", label: "Chile" }, { code: "CN", label: "China" }, { code: "CX", label: "Christmas Island" },
  { code: "CC", label: "Cocos (Keeling) Islands" }, { code: "CO", label: "Colombia" }, { code: "KM", label: "Comoros" },
  { code: "CG", label: "Congo" }, { code: "CD", label: "Congo (Democratic Republic)" }, { code: "CK", label: "Cook Islands" },
  { code: "CR", label: "Costa Rica" }, { code: "CI", label: "Côte d'Ivoire" }, { code: "HR", label: "Croatia" },
  { code: "CU", label: "Cuba" }, { code: "CW", label: "Curaçao" }, { code: "CY", label: "Cyprus" },
  { code: "CZ", label: "Czechia" }, { code: "DK", label: "Denmark" }, { code: "DJ", label: "Djibouti" },
  { code: "DM", label: "Dominica" }, { code: "DO", label: "Dominican Republic" }, { code: "EC", label: "Ecuador" },
  { code: "EG", label: "Egypt" }, { code: "SV", label: "El Salvador" }, { code: "GQ", label: "Equatorial Guinea" },
  { code: "ER", label: "Eritrea" }, { code: "EE", label: "Estonia" }, { code: "SZ", label: "Eswatini" },
  { code: "ET", label: "Ethiopia" }, { code: "FK", label: "Falkland Islands" }, { code: "FO", label: "Faroe Islands" },
  { code: "FJ", label: "Fiji" }, { code: "FI", label: "Finland" }, { code: "FR", label: "France" },
  { code: "GF", label: "French Guiana" }, { code: "PF", label: "French Polynesia" }, { code: "TF", label: "French Southern Territories" },
  { code: "GA", label: "Gabon" }, { code: "GM", label: "Gambia" }, { code: "GE", label: "Georgia" },
  { code: "DE", label: "Germany" }, { code: "GH", label: "Ghana" }, { code: "GI", label: "Gibraltar" },
  { code: "GR", label: "Greece" }, { code: "GL", label: "Greenland" }, { code: "GD", label: "Grenada" },
  { code: "GP", label: "Guadeloupe" }, { code: "GU", label: "Guam" }, { code: "GT", label: "Guatemala" },
  { code: "GG", label: "Guernsey" }, { code: "GN", label: "Guinea" }, { code: "GW", label: "Guinea-Bissau" },
  { code: "GY", label: "Guyana" }, { code: "HT", label: "Haiti" }, { code: "HM", label: "Heard and McDonald Islands" },
  { code: "VA", label: "Holy See (Vatican)" }, { code: "HN", label: "Honduras" }, { code: "HK", label: "Hong Kong" },
  { code: "HU", label: "Hungary" }, { code: "IS", label: "Iceland" }, { code: "IN", label: "India" },
  { code: "ID", label: "Indonesia" }, { code: "IR", label: "Iran" }, { code: "IQ", label: "Iraq" },
  { code: "IE", label: "Ireland" }, { code: "IM", label: "Isle of Man" }, { code: "IL", label: "Israel" },
  { code: "IT", label: "Italy" }, { code: "JM", label: "Jamaica" }, { code: "JP", label: "Japan" },
  { code: "JE", label: "Jersey" }, { code: "JO", label: "Jordan" }, { code: "KZ", label: "Kazakhstan" },
  { code: "KE", label: "Kenya" }, { code: "KI", label: "Kiribati" }, { code: "KP", label: "Korea (North)" },
  { code: "KR", label: "Korea (South)" }, { code: "KW", label: "Kuwait" }, { code: "KG", label: "Kyrgyzstan" },
  { code: "LA", label: "Laos" }, { code: "LV", label: "Latvia" }, { code: "LB", label: "Lebanon" },
  { code: "LS", label: "Lesotho" }, { code: "LR", label: "Liberia" }, { code: "LY", label: "Libya" },
  { code: "LI", label: "Liechtenstein" }, { code: "LT", label: "Lithuania" }, { code: "LU", label: "Luxembourg" },
  { code: "MO", label: "Macao" }, { code: "MG", label: "Madagascar" }, { code: "MW", label: "Malawi" },
  { code: "MY", label: "Malaysia" }, { code: "MV", label: "Maldives" }, { code: "ML", label: "Mali" },
  { code: "MT", label: "Malta" }, { code: "MH", label: "Marshall Islands" }, { code: "MQ", label: "Martinique" },
  { code: "MR", label: "Mauritania" }, { code: "MU", label: "Mauritius" }, { code: "YT", label: "Mayotte" },
  { code: "MX", label: "Mexico" }, { code: "FM", label: "Micronesia" }, { code: "MD", label: "Moldova" },
  { code: "MC", label: "Monaco" }, { code: "MN", label: "Mongolia" }, { code: "ME", label: "Montenegro" },
  { code: "MS", label: "Montserrat" }, { code: "MA", label: "Morocco" }, { code: "MZ", label: "Mozambique" },
  { code: "MM", label: "Myanmar" }, { code: "NA", label: "Namibia" }, { code: "NR", label: "Nauru" },
  { code: "NP", label: "Nepal" }, { code: "NL", label: "Netherlands" }, { code: "NC", label: "New Caledonia" },
  { code: "NZ", label: "New Zealand" }, { code: "NI", label: "Nicaragua" }, { code: "NE", label: "Niger" },
  { code: "NG", label: "Nigeria" }, { code: "NU", label: "Niue" }, { code: "NF", label: "Norfolk Island" },
  { code: "MK", label: "North Macedonia" }, { code: "MP", label: "Northern Mariana Islands" }, { code: "NO", label: "Norway" },
  { code: "OM", label: "Oman" }, { code: "PK", label: "Pakistan" }, { code: "PW", label: "Palau" },
  { code: "PS", label: "Palestine" }, { code: "PA", label: "Panama" }, { code: "PG", label: "Papua New Guinea" },
  { code: "PY", label: "Paraguay" }, { code: "PE", label: "Peru" }, { code: "PH", label: "Philippines" },
  { code: "PN", label: "Pitcairn" }, { code: "PL", label: "Poland" }, { code: "PT", label: "Portugal" },
  { code: "PR", label: "Puerto Rico" }, { code: "QA", label: "Qatar" }, { code: "RE", label: "Réunion" },
  { code: "RO", label: "Romania" }, { code: "RU", label: "Russia" }, { code: "RW", label: "Rwanda" },
  { code: "BL", label: "Saint Barthélemy" }, { code: "SH", label: "Saint Helena" }, { code: "KN", label: "Saint Kitts and Nevis" },
  { code: "LC", label: "Saint Lucia" }, { code: "MF", label: "Saint Martin (French)" }, { code: "PM", label: "Saint Pierre and Miquelon" },
  { code: "VC", label: "Saint Vincent and the Grenadines" }, { code: "WS", label: "Samoa" }, { code: "SM", label: "San Marino" },
  { code: "ST", label: "São Tomé and Príncipe" }, { code: "SA", label: "Saudi Arabia" }, { code: "SN", label: "Senegal" },
  { code: "RS", label: "Serbia" }, { code: "SC", label: "Seychelles" }, { code: "SL", label: "Sierra Leone" },
  { code: "SG", label: "Singapore" }, { code: "SX", label: "Sint Maarten (Dutch)" }, { code: "SK", label: "Slovakia" },
  { code: "SI", label: "Slovenia" }, { code: "SB", label: "Solomon Islands" }, { code: "SO", label: "Somalia" },
  { code: "ZA", label: "South Africa" }, { code: "GS", label: "South Georgia / Sandwich Islands" }, { code: "SS", label: "South Sudan" },
  { code: "ES", label: "Spain" }, { code: "LK", label: "Sri Lanka" }, { code: "SD", label: "Sudan" },
  { code: "SR", label: "Suriname" }, { code: "SJ", label: "Svalbard and Jan Mayen" }, { code: "SE", label: "Sweden" },
  { code: "CH", label: "Switzerland" }, { code: "SY", label: "Syria" }, { code: "TW", label: "Taiwan" },
  { code: "TJ", label: "Tajikistan" }, { code: "TZ", label: "Tanzania" }, { code: "TH", label: "Thailand" },
  { code: "TL", label: "Timor-Leste" }, { code: "TG", label: "Togo" }, { code: "TK", label: "Tokelau" },
  { code: "TO", label: "Tonga" }, { code: "TT", label: "Trinidad and Tobago" }, { code: "TN", label: "Tunisia" },
  { code: "TR", label: "Türkiye" }, { code: "TM", label: "Turkmenistan" }, { code: "TC", label: "Turks and Caicos Islands" },
  { code: "TV", label: "Tuvalu" }, { code: "UG", label: "Uganda" }, { code: "UA", label: "Ukraine" },
  { code: "AE", label: "United Arab Emirates" }, { code: "UM", label: "U.S. Minor Outlying Islands" },
  { code: "UY", label: "Uruguay" }, { code: "UZ", label: "Uzbekistan" }, { code: "VU", label: "Vanuatu" },
  { code: "VE", label: "Venezuela" }, { code: "VN", label: "Vietnam" }, { code: "VG", label: "Virgin Islands (British)" },
  { code: "VI", label: "Virgin Islands (U.S.)" }, { code: "WF", label: "Wallis and Futuna" }, { code: "EH", label: "Western Sahara" },
  { code: "YE", label: "Yemen" }, { code: "ZM", label: "Zambia" }, { code: "ZW", label: "Zimbabwe" },
];

const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AS", "American Samoa"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["GU", "Guam"], ["HI", "Hawaii"],
  ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"],
  ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"],
  ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
  ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"],
  ["MP", "Northern Mariana Islands"], ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"],
  ["PA", "Pennsylvania"], ["PR", "Puerto Rico"], ["RI", "Rhode Island"],
  ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"],
  ["UT", "Utah"], ["VT", "Vermont"], ["VI", "U.S. Virgin Islands"], ["VA", "Virginia"], ["WA", "Washington"],
  ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
];

const CA_PROVINCES = [
  ["AB", "Alberta"], ["BC", "British Columbia"], ["MB", "Manitoba"],
  ["NB", "New Brunswick"], ["NL", "Newfoundland and Labrador"],
  ["NS", "Nova Scotia"], ["NT", "Northwest Territories"], ["NU", "Nunavut"],
  ["ON", "Ontario"], ["PE", "Prince Edward Island"], ["QC", "Quebec"],
  ["SK", "Saskatchewan"], ["YT", "Yukon"],
];

export const EMPTY_ADDRESS = {
  line1: "", line2: "", city: "", state: "", postal_code: "", country: "US",
};

/**
 * Compose a structured address into a single human-readable line so the
 * backend geocoder + emails + ICS files have a consistent string to use.
 * Skips empty parts cleanly: "123 Main, Apt 4, Brooklyn, NY 11201, US".
 */
export function composeAddress(a) {
  if (!a) return "";
  const parts = [];
  if (a.line1) parts.push(a.line1);
  if (a.line2) parts.push(a.line2);
  if (a.city) parts.push(a.city);
  const stateZip = [a.state, a.postal_code].filter(Boolean).join(" ");
  if (stateZip) parts.push(stateZip);
  if (a.country) parts.push(a.country);
  return parts.join(", ");
}

export function AddressFields({ value, onChange, testid = "addr", className = "" }) {
  // Treat missing/legacy values as the empty shape so the form is usable
  // even when editing pre-Bundle-B.1 records that only had a flat `address`.
  const v = { ...EMPTY_ADDRESS, ...(value || {}) };
  const set = (patch) => onChange({ ...v, ...patch });
  // Use the structured state dropdown for US/CA where we have the full
  // subdivision list; everywhere else, fall back to a text input so the
  // visitor can type "Île-de-France" or "Bavaria" without a US-centric UI.
  const isUS = v.country === "US";
  const isCA = v.country === "CA";
  const hasDropdownState = isUS || isCA;
  const states = isUS ? US_STATES : isCA ? CA_PROVINCES : [];

  return (
    <div className={`grid grid-cols-2 gap-3 ${className}`}>
      <div className="col-span-2">
        <Lbl>Address line 1</Lbl>
        <input
          className="bloq-input"
          placeholder="Street number and name"
          value={v.line1}
          onChange={(e) => set({ line1: e.target.value })}
          data-testid={`${testid}-line1`}
        />
      </div>
      <div className="col-span-2">
        <Lbl>Address line 2 <span className="normal-case tracking-normal text-[#82828c]">(optional)</span></Lbl>
        <input
          className="bloq-input"
          placeholder="Apartment, suite, unit, etc."
          value={v.line2}
          onChange={(e) => set({ line2: e.target.value })}
          data-testid={`${testid}-line2`}
        />
      </div>
      <div>
        <Lbl>City</Lbl>
        <input
          className="bloq-input"
          value={v.city}
          onChange={(e) => set({ city: e.target.value })}
          data-testid={`${testid}-city`}
        />
      </div>
      <div>
        <Lbl>{isCA ? "Province" : "State"}</Lbl>
        {hasDropdownState ? (
          <Select value={v.state || ""} onValueChange={(s) => set({ state: s })}>
            <SelectTrigger data-testid={`${testid}-state`}>
              <SelectValue placeholder={isCA ? "Choose province" : "Choose state"} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {states.map(([code, label]) => (
                <SelectItem key={code} value={code}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <input
            className="bloq-input"
            placeholder="State / Province / Region"
            value={v.state}
            onChange={(e) => set({ state: e.target.value })}
            data-testid={`${testid}-state`}
          />
        )}
      </div>
      <div>
        <Lbl>Postal code</Lbl>
        <input
          className="bloq-input"
          value={v.postal_code}
          onChange={(e) => set({ postal_code: e.target.value })}
          data-testid={`${testid}-postal-code`}
        />
      </div>
      <div>
        <Lbl>Country</Lbl>
        <Select value={v.country || "US"} onValueChange={(c) => set({ country: c, state: "" })}>
          <SelectTrigger data-testid={`${testid}-country`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {COUNTRIES.map((c) =>
              c.code === "__DIVIDER__" ? (
                <div key="div" className="px-2 py-1 text-[10px] uppercase tracking-widest text-[#c4c4cb]" aria-hidden="true">All countries</div>
              ) : (
                <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function Lbl({ children }) {
  return (
    <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">
      {children}
    </label>
  );
}
